import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useAuth } from "@/context/auth/AuthContext";
import { Facebook } from "lucide-react";
import { signInWithGoogle, signInWithFacebook } from "@/services/auth.service";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import { useInitialSync } from "@/hooks/useInitialSync";
import { SyncStatusIndicator } from "@/components/ui/SyncStatusIndicator";
import logger from "@/utils/logger";
import { safeToastError, safeToastSuccess } from "@/components/ui/toastManager";
import { useViewportHeight } from "@/hooks/useViewportHeight";
import { useKeyboardAwareScrolling } from "@/hooks/useKeyboardAwareScrolling";
import { useResetModals } from "@/hooks/useResetModals";

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user, isLoading, authInitialized } = useAuth();
  const { theme } = useThemeConfig();
  const { isSyncing, triggerSync } = useInitialSync();
  useResetModals();

  const containerRef = useRef<HTMLDivElement>(null);
  
  // Usa i nuovi hook per viewport dinamico e keyboard-aware scrolling
  useViewportHeight();
  const { isKeyboardOpen } = useKeyboardAwareScrolling(containerRef, {
    offsetTop: 100,
    offsetBottom: 160,
    behavior: 'smooth'
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [showDelayedMessage, setShowDelayedMessage] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const from = location.state?.from?.pathname || "/";

  // Reset submit state when auth completes or stalls
  useEffect(() => {
    const reset = () => setIsSubmitting(false);
    window.addEventListener('userSignedIn', reset as EventListener);
    window.addEventListener('userSignedOut', reset as EventListener);
    return () => {
      window.removeEventListener('userSignedIn', reset as EventListener);
      window.removeEventListener('userSignedOut', reset as EventListener);
    };
  }, []);

  // If auth state changes (user or loading), ensure we exit submitting mode
  useEffect(() => {
    if (!isLoading) {
      setIsSubmitting(false);
    }
  }, [user, isLoading]);

  // Fallback: auto-reset after 5s to avoid stuck buttons
  useEffect(() => {
    if (!isSubmitting) return;
    const timeout = setTimeout(() => setIsSubmitting(false), 5000);
    return () => clearTimeout(timeout);
  }, [isSubmitting]);

  // ✅ NUOVO: Listener per eventi di sync dopo login
  useEffect(() => {
    const handleUserSignedIn = (event: CustomEvent) => {
      logger.log('[LOGIN_PAGE] User signed in, sync will be triggered automatically');
    };

    const handleSessionRestored = (event: CustomEvent) => {
      logger.log('[LOGIN_PAGE] User session restored, checking sync status');
    };

    window.addEventListener('userSignedIn', handleUserSignedIn as EventListener);
    window.addEventListener('userSessionRestored', handleSessionRestored as EventListener);

    return () => {
      window.removeEventListener('userSignedIn', handleUserSignedIn as EventListener);
      window.removeEventListener('userSessionRestored', handleSessionRestored as EventListener);
    };
  }, []);

  // Monitor authentication state and redirect if needed
  useEffect(() => {
    if (authInitialized && !isLoading && user) {
      logger.log("[LOGIN_PAGE] User authenticated, redirecting to", from);

      // Always redirect within the SPA to preserve routing state
      navigate(from, { replace: true });

      // On mobile devices also trigger the native deep link. The timeout gives
      // the router time to complete before the browser hands control to the app.
      const isMobile = navigator.userAgent.toLowerCase().includes("android") ||
                       navigator.userAgent.toLowerCase().includes("iphone");
      if (isMobile) {
        setTimeout(() => {
          window.location.href = "ceralia://login-success";
        }, 500);
      }
    }
  }, [user, isLoading, authInitialized, navigate, from]);

    // Mostra un messaggio se la fase di caricamento dura troppo
    useEffect(() => {
      if (isLoading) {
        const timeout = setTimeout(() => setShowDelayedMessage(true), 3000);
        return () => clearTimeout(timeout);
      }
      setShowDelayedMessage(false);
    }, [isLoading]);

    // Show loader while checking authentication or syncing
    if (isLoading) {
      return (
        <div className="min-viewport-height flex items-center justify-center" style={{
          backgroundColor: theme.backgroundColor,
          color: theme.textColor
        }}>
          <div className="text-center">
            <h1
              className="text-2xl font-bold mb-4"
              style={{ color: theme.sectionTitleColor }}
            >
              Verifica autenticazione...
            </h1>
            <p style={{ color: theme.sectionTextColor }}>
              Attendere mentre controlliamo la sessione
            </p>
            {isSyncing && (
              <div className="mt-4">
                <SyncStatusIndicator showDetails />
              </div>
            )}
            {showDelayedMessage && (
              <p className="mt-4 text-sm" style={{ color: theme.sectionTextColor }}>
                Operazione in corso, potrebbe richiedere qualche secondo...
              </p>
            )}
          </div>
        </div>
      );
    }

  // Handle login
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    setInlineError(null);
    if (!email || !password) {
      const missingCredentialsMessage = "Inserisci email e password";
      setInlineError(missingCredentialsMessage);
      safeToastError(missingCredentialsMessage);
      return;
    }
    
    setIsSubmitting(true);

    try {
      logger.log("Tentativo di login con:", email);
      const { success, error, handled } = await login(email, password, rememberMe);

      if (!success) {
        const msg = error?.toLowerCase() || "";
        const genericErrorMessage = "Credenziali non valide. Controlla email e password.";

        // Evidenziamo l'errore vicino al pulsante per aumentare la visibilità (richiesta UX).
        setInlineError(error || genericErrorMessage);

        if (!handled) {
          if (msg.includes('dispositivo non può essere registrato')) {
            const deviceLimitMessage =
              error ||
              'Il dispositivo non può essere registrato: limite raggiunto per il tuo piano.';
            setInlineError(deviceLimitMessage);
            safeToastError(deviceLimitMessage);
          } else if (msg.includes('troppi tentativi')) {
            safeToastError(error);
          } else {
            safeToastError(genericErrorMessage);
          }
        }
      } else {
        safeToastSuccess("Accesso effettuato con successo!");
        setInlineError(null);
        // Il sync sarà triggerato automaticamente dall'AuthStateListener
      }
    } catch (error: any) {
      console.error("Errore durante il login:", error);
      const unexpectedErrorMessage =
        error.message || "Si è verificato un errore durante l'accesso. Riprova più tardi.";
      setInlineError(unexpectedErrorMessage);
      safeToastError(unexpectedErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
      // Redirect happens automatically via Supabase
    } catch (error: any) {
      console.error("Errore login Google:", error);
      const googleErrorMessage = error.message || "Errore durante il login con Google.";
      setInlineError(googleErrorMessage);
      safeToastError(googleErrorMessage);
      setIsSubmitting(false);
    }
  };

  const handleFacebookLogin = async () => {
    setIsSubmitting(true);
    try {
      await signInWithFacebook();
      // Redirect happens automatically via Supabase
    } catch (error: any) {
      console.error("Errore login Facebook:", error);
      const facebookErrorMessage = error.message || "Errore durante il login con Facebook.";
      setInlineError(facebookErrorMessage);
      safeToastError(facebookErrorMessage);
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-viewport-height keyboard-aware-scroll flex flex-col p-6 pb-safe viewport-transition"
      style={{
        backgroundColor: theme.backgroundColor,
        color: theme.textColor
      }}
    >
      <div className="mx-auto w-full max-w-md flex flex-col">
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <img 
              src="/logo.png" 
              alt="Logo Ceralia" 
              className="h-24 w-24 object-contain" 
              draggable={false} 
            />
          </div>

          <h1
            className="text-3xl font-bold"
            style={{ color: theme.sectionTitleColor }}
          >
            Ceralia<br/>Spesa Semplice
          </h1>
          <p className="mt-2" style={{ color: theme.sectionTextColor }}>
            Accedi al tuo account per gestire le tue liste
          </p>
        </div>

        {/* ✅ NUOVO: Indicatore di stato sync durante il login */}
        {(isSyncing || user) && (
          <div
            className="mb-4 p-3 rounded-lg border"
            style={{ backgroundColor: `${theme.primaryColor}20`, borderColor: theme.primaryColor }}
          >
            <SyncStatusIndicator showDetails className="justify-center" />
          </div>
        )}

        <div
          className="p-6 flex-shrink-0"
          style={{
            backgroundColor: theme.cardBackgroundColor,
            borderRadius: theme.cardBorderRadius || theme.borderRadius,
            boxShadow: theme.cardShadow,
            padding: theme.spacingBase
          }}
        >
          <form method="post" action="/auth/login" autoComplete="on" onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="username"
                type="email"
                autoComplete="username"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="tuaemail@esempio.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
                required
                disabled={isSubmitting}
                data-keyboard-scroll
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1"
                required
                disabled={isSubmitting}
                data-keyboard-scroll
              />
            </div>

            <div className="flex items-center">
              <div className="ml-auto">
                <Link to="/forgot-password" className="text-xs hover:underline" style={{
                  color: theme.primaryColor
                }}>
                  Password dimenticata?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              style={{
                backgroundColor: theme.primaryColor,
                borderRadius: theme.buttonBorderRadius,
                padding: theme.buttonPadding,
                color: theme.textPrimaryColor,
                fontWeight: 'bold',
                transitionDuration: theme.transitionSpeed
              }}
              disabled={isSubmitting || (user && isSyncing)}
            >
              {isSubmitting ? "Accesso in corso..." : "Accedi"}
            </Button>

            {inlineError && (
              <p
                className="text-sm font-medium text-red-500 text-center"
                role="alert"
              >
                {inlineError}
              </p>
            )}

            <p
              className="mt-2 text-xs text-center"
              style={{ color: theme.sectionTextColor }}
            >
              La registrazione del dispositivo è facoltativa e non influisce sull'accesso.
            </p>

            <div className="text-xs text-center mt-4" style={{ color: theme.sectionTextColor }}>
              Utilizzando Ceralia - Spesa Semplice, accetti le{' '}
              <a
                href="https://www.ceralia.it/license"
                className="hover:underline"
                style={{ color: theme.primaryColor }}
              >
                Condizioni d'uso
              </a>{' '}
              e l'
              <a
                href="https://www.ceralia.it/privacy-policy"
                className="hover:underline"
                style={{ color: theme.primaryColor }}
              >
                Informativa sulla privacy
              </a>
              .
            </div>
          </form>

          <Separator className="my-3" />

          <div className="text-center">
            <p className="text-sm" style={{ color: theme.sectionTextColor }}>
              Non hai un account?{" "}
              <Link to="/register" className="hover:underline" style={{
                color: theme.primaryColor
              }}>
                Registrati
              </Link>
            </p>
          </div>
        </div>
        
        {/* Spazio aggiuntivo per assicurare che tutto sia scorrevole */}
        <div className="h-32 flex-shrink-0" />
      </div>
    </div>
  );
};

export default LoginPage;
